<script setup lang="ts">
import ColorInput from "@/components/common/Elements/ColorInput.vue";
import PixelNumber from "@/components/common/Elements/PixelNumber.vue";
import MarginPaddingInput from "@/components/generic/MarginPaddingInput.vue";
import {
  CARD_HEIGHT_MAX,
  CARD_WIDTH_MAX,
  CARD_WIDTH_MIN,
  CORNER_RADIUS_MAX,
  type BoxSides,
  type MarginSide,
  type ScheduleAppearance,
  type ShadowValue,
} from "@/util/formSchedule";
import { ChevronDownIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UISelect, UISwitch, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  appearance: ScheduleAppearance;
}>();

const emit = defineEmits<{
  (e: "update:appearance", value: ScheduleAppearance): void;
}>();

const open = ref(true);

type SelectOption = { label: string; value: string; [k: string]: string };

const shadowOptions = computed<SelectOption[]>(() => [
  { label: t("form.scheduleVisibility.appearance.shadowOptions.none"), value: "none" },
  { label: t("form.scheduleVisibility.appearance.shadowOptions.soft"), value: "soft" },
  { label: t("form.scheduleVisibility.appearance.shadowOptions.elevated"), value: "elevated" },
]);

const patch = (partial: Partial<ScheduleAppearance>) => {
  emit("update:appearance", { ...props.appearance, ...partial });
};

// ColorInput emits an uppercase hex without a leading "#"; the config stores it with one.
const withHash = (hex: string): string => (hex ? (hex.startsWith("#") ? hex : `#${hex}`) : "#ffffff");

const space = computed(() => ({
  padding: props.appearance.padding,
  margin: props.appearance.margin,
}));

const onUpdateSpace = (
  type: "padding" | "margin",
  side: keyof BoxSides,
  value: number | string
) => {
  if (type === "margin") {
    const marginValue: MarginSide = value === "auto" ? "auto" : Math.max(0, Number(value) || 0);
    emit("update:appearance", {
      ...props.appearance,
      margin: { ...props.appearance.margin, [side]: marginValue },
    });
    return;
  }
  emit("update:appearance", {
    ...props.appearance,
    padding: { ...props.appearance.padding, [side]: value === "auto" ? 0 : Number(value) || 0 },
  });
};
</script>

<template>
  <div class="appearance">
    <button
      type="button"
      class="appearance__header"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="appearance__title">{{ t("form.scheduleVisibility.appearance.title") }}</span>
      <ChevronDownIcon
        class="h-4 w-4 text-gray-400 transition-transform"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <div v-if="open" class="appearance__body">
      <!-- Row 1: card bg / page bg / corner radius -->
      <div class="appearance__row">
        <ColorInput
          :label="t('form.scheduleVisibility.appearance.cardBackground')"
          :compact="true"
          :color="appearance.cardBackground"
          @update:color="patch({ cardBackground: withHash($event) })"
        />
        <ColorInput
          :label="t('form.scheduleVisibility.appearance.pageBackground')"
          :compact="true"
          :color="appearance.pageBackground"
          @update:color="patch({ pageBackground: withHash($event) })"
        />
        <PixelNumber
          :label="t('form.scheduleVisibility.appearance.cornerRadius')"
          :value="appearance.cornerRadius"
          :min="0"
          :max="CORNER_RADIUS_MAX"
          @update:value="patch({ cornerRadius: $event })"
        />
      </div>

      <!-- Row 2: card width / card height / shadow -->
      <div class="appearance__row">
        <PixelNumber
          :label="t('form.scheduleVisibility.appearance.cardWidth')"
          :value="appearance.cardWidth"
          :min="CARD_WIDTH_MIN"
          :max="CARD_WIDTH_MAX"
          @update:value="patch({ cardWidth: $event })"
        />
        <PixelNumber
          :label="t('form.scheduleVisibility.appearance.cardHeight')"
          :value="appearance.cardHeight"
          :min="0"
          :max="CARD_HEIGHT_MAX"
          @update:value="patch({ cardHeight: $event })"
        />
        <div class="field">
          <UITextXsMedium>{{ t("form.scheduleVisibility.appearance.shadow") }}</UITextXsMedium>
          <UISelect
            id="appearance-shadow"
            class="w-full"
            size="large"
            :value="appearance.shadow"
            :options="shadowOptions"
            @update:value="patch({ shadow: $event as ShadowValue })"
          />
        </div>
      </div>

      <!-- Margin & padding box-model editor (reused from the On Submit section) -->
      <MarginPaddingInput
        :label="t('form.scheduleVisibility.appearance.marginPadding')"
        :space="space"
        :padding-units-config="['px']"
        :margin-units-config="['px', 'auto']"
        @updateSpace="onUpdateSpace"
      />

      <!-- Miscellaneous: agency branding -->
      <div class="misc">
        <p class="field__label">{{ t("form.scheduleVisibility.appearance.miscellaneous") }}</p>
        <div class="misc__row">
          <UISwitch
            id="appearance-branding"
            :value="appearance.agencyBranding"
            @update:value="patch({ agencyBranding: $event })"
          />
          <div>
            <p class="misc__title">{{ t("form.scheduleVisibility.appearance.agencyBranding") }}</p>
            <p class="misc__help">
              {{ t("form.scheduleVisibility.appearance.agencyBrandingHelp") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.appearance {
  border: 1px solid #eaecf0;
  border-radius: 12px;
  overflow: hidden;
}

.appearance__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  background: #ffffff;
  border: 0;
  cursor: pointer;
  outline: none;
}

.appearance__header:focus-visible {
  box-shadow: inset 0 0 0 2px #155eef;
}

.appearance__title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}

.appearance__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  border-top: 1px solid #eaecf0;
}

.appearance__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: start;
}

/* The shared ColorInput trigger is intrinsically 48px; pin it to the same
   40px height as the neighboring number inputs and select (matches design). */
.appearance__row :deep(.color-picker-container) {
  height: 40px;
  padding-top: 0;
  padding-bottom: 0;
}

/* HighRise sizes the swatch tile via inline CSS variables (32px / 4px);
   the design uses a 22px swatch with a 6px radius inside the 40px trigger. */
.appearance__row :deep(.hr-swatch-tile__pad) {
  --n-width: 22px !important;
  --n-height: 22px !important;
  --n-border-radius: 6px !important;
}

.field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
}

.field__label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.misc {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.misc__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.misc__title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.misc__help {
  font-size: 12px;
  color: #667085;
}
</style>
